import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { money, fmt, errMsg } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { ORDER_STATUSES } from '../../utils/statuses';
import { Spinner, Badge } from '../../components/common/UI';
import {
  Package, Wrench, Truck, TrendingDown, TrendingUp,
  AlertCircle, CheckCircle, Clock, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Карточка метрики ───────────────────────────────────────────
function MetricCard({ title, value, sub, icon: Icon, color = 'text-gray-200', bg = 'bg-gray-800/60' }) {
  return (
    <div className={`${bg} border border-gray-800 rounded-xl p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{title}</div>
          <div className={`text-2xl font-bold mono ${color}`}>{value}</div>
          {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon size={20} className="text-gray-600 mt-0.5" />}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { isProducer, user } = useAuth();
  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expandedDealer, setExpandedDealer] = useState(null);

  // Фильтры
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterDealer, setFilterDealer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dealerList, setDealerList] = useState([]);

  const buildParams = () => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to = dateTo;
    // Дилер видит только свои данные
    if (!isProducer && user?.dealer_id) p.dealer_id = user.dealer_id;
    return p;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [{ data: s }, dealersResp, dealerListResp] = await Promise.all([
        api.get('/reports/summary', { params }),
        isProducer ? api.get('/reports/dealers', { params }) : Promise.resolve({ data: [] }),
        isProducer ? api.get('/admin/dealers') : Promise.resolve({ data: [] }),
      ]);
      setSummary(s);
      setDealers(dealersResp.data || []);
      setDealerList(dealerListResp.data || []);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, isProducer]);

  const loadOrders = useCallback(async () => {
    try {
      const params = buildParams();
      if (filterDealer) params.dealer_id = filterDealer;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get('/reports/orders', { params });
      setOrders(data);
    } catch (err) { toast.error(errMsg(err)); }
  }, [dateFrom, dateTo, filterDealer, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

  const STATUS_OPTS = [{ value: '', label: 'Все статусы' },
    ...Object.entries(ORDER_STATUSES).map(([v, d]) => ({ value: v, label: d.label }))];

  const DEALER_OPTS = [{ value: '', label: 'Все дилеры' },
    ...dealerList.map(d => ({ value: d.id, label: d.name }))];

  const TAB_ITEMS = [
    { id: 'summary', label: 'Сводка' },
    ...(isProducer ? [{ id: 'dealers', label: 'По дилерам' }] : []),
    { id: 'orders',  label: 'Детализация заказов' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-100">Отчёты</h1>
          <p className="text-xs text-gray-600 mt-0.5">Сводная аналитика по заказам, ремонтам и поставкам</p>
        </div>
        <button className="btn-ghost p-2" onClick={load} title="Обновить">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Фильтр дат */}
      <div className="flex flex-wrap gap-3 items-center p-3 bg-gray-800/40 rounded-xl border border-gray-800">
        <span className="text-xs text-gray-500 shrink-0">Период:</span>
        <div className="flex items-center gap-2">
          <input type="date" className="input py-1.5 text-sm" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} />
          <span className="text-gray-600 text-sm">—</span>
          <input type="date" className="input py-1.5 text-sm" value={dateTo}
            onChange={e => setDateTo(e.target.value)} />
        </div>
        {(dateFrom || dateTo) && (
          <button className="text-xs text-gray-500 hover:text-gray-300"
            onClick={() => { setDateFrom(''); setDateTo(''); }}>
            Сбросить
          </button>
        )}
        {(dateFrom || dateTo) && (
          <span className="text-xs text-blue-400">
            {dateFrom && fmt(dateFrom + 'T00:00:00')} — {dateTo && fmt(dateTo + 'T00:00:00')}
          </span>
        )}
      </div>

      {/* Табы */}
      <div className="flex gap-1 border-b border-gray-800">
        {TAB_ITEMS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
              (tab === t.id ? 'border-blue-500 text-blue-300' : 'border-transparent text-gray-500 hover:text-gray-300')}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size={28} /></div>
      ) : (

        <>
          {/* ── СВОДКА ── */}
          {tab === 'summary' && summary && (
            <div className="space-y-6">
              {/* Заказы */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Package size={13} /> Заказы
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard title="Всего заказов" value={summary.orders.total} icon={Package} />
                  <MetricCard title="Стоимость заказов" value={money(summary.orders.total_retail)} icon={TrendingUp}
                    color="text-sky-300" />
                  <MetricCard title="Текущая стоимость заказов" value={money(summary.orders.total_amount)} icon={TrendingDown}
                    color="text-blue-300" />
                  <MetricCard title="Оплачено" value={money(summary.orders.paid)} icon={CheckCircle}
                    color="text-green-400" />
                  <MetricCard title="Не оплачено по заказам" value={money(summary.orders.debt)} icon={AlertCircle}
                    color={summary.orders.debt > 0 ? 'text-red-400' : 'text-gray-400'} />
                  <MetricCard title="Полностью отгружено" value={summary.orders.shipped_full || 0} icon={Truck}
                    color="text-purple-300" />
                  <MetricCard title="Полностью оплачено" value={summary.orders.paid_full || 0} icon={CheckCircle}
                    color="text-green-300" />
                  <MetricCard title="Не оплачено" value={summary.orders.unpaid_count || 0} icon={AlertCircle}
                    color="text-red-300" />
                </div>

                {/* Статусы */}
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { key: 'placed',     label: 'Размещён' },
                    { key: 'production', label: 'Изготовление' },
                    { key: 'ready',      label: 'Готов' },
                    { key: 'shipped',    label: 'Отгружен' },
                  ].map(s => (
                    <div key={s.key} className="flex items-center justify-between p-2.5 bg-gray-800/40 rounded-lg border border-gray-800 text-sm">
                      <span className="text-gray-500">{s.label}</span>
                      <span className="font-medium text-gray-200 mono">{summary.orders[s.key] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ремонт */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Wrench size={13} /> Ремонт
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <MetricCard title="Всего" value={summary.repairs.total} icon={Wrench} />
                  <MetricCard title="Оформлено" value={summary.repairs.pending || 0} icon={Clock}
                    color="text-gray-200" />
                  <MetricCard title="Принято" value={summary.repairs.received || 0} icon={Clock} />
                  <MetricCard title="Диагностика" value={summary.repairs.diagnostics || 0} icon={AlertCircle} />
                  <MetricCard title="В ремонте" value={summary.repairs.in_repair || 0} icon={Wrench}
                    color="text-yellow-300" />
                </div>
              </div>

              {/* Поставки */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Truck size={13} /> Поставки
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MetricCard title="Всего" value={summary.supplies.total} icon={Truck} />
                  <MetricCard title="Активных" value={summary.supplies.active} icon={Truck} />
                  <MetricCard title="Сумма поставок" value={money(summary.supplies.total_amount)} icon={TrendingUp}
                    color="text-blue-300" />
                </div>
              </div>
            </div>
          )}

          {/* ── ПО ДИЛЕРАМ ── */}
          {tab === 'dealers' && isProducer && (
            <div className="space-y-2">
              {/* Итоговая строка */}
              {dealers.length > 0 && (
                <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 uppercase tracking-wide px-3 pb-1">
                  <div className="col-span-2">Дилер</div>
                  <div className="text-center">Заказов</div>
                  <div className="text-right">Сумма</div>
                  <div className="text-right">Оплачено</div>
                  <div className="text-right">Долг</div>
                  <div className="text-center">Ремонт / Поставки</div>
                </div>
              )}
              {dealers.map(d => {
                const expanded = expandedDealer === d.id;
                const hasDebt = d.orders_debt > 0;
                return (
                  <div key={d.id} className="bg-gray-800/60 border border-gray-800 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-7 gap-2 items-center px-3 py-3 cursor-pointer hover:bg-gray-800/80 transition-colors"
                      onClick={() => setExpandedDealer(expanded ? null : d.id)}>
                      <div className="col-span-2 flex items-center gap-2">
                        <button className="text-gray-500">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button>
                        <span className="font-medium text-gray-200">{d.dealer_name}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-gray-300">{d.orders_count}</span>
                        {+d.orders_active > 0 && <span className="text-xs text-blue-400 ml-1">({d.orders_active} акт.)</span>}
                      </div>
                      <div className="text-right mono text-gray-300">{money(d.orders_amount)}</div>
                      <div className="text-right mono text-green-400">{money(d.orders_paid)}</div>
                      <div className={'text-right mono font-medium ' + (hasDebt ? 'text-red-400' : 'text-gray-500')}>
                        {hasDebt ? money(d.orders_debt) : '—'}
                      </div>
                      <div className="text-center text-xs text-gray-500">
                        {d.repairs_count > 0 && <span className="mr-2">🔧 {d.repairs_count}</span>}
                        {d.supplies_count > 0 && <span>📦 {d.supplies_count}</span>}
                      </div>
                    </div>

                    {/* Детализация по дилеру */}
                    {expanded && (
                      <div className="border-t border-gray-700 bg-gray-900/30 px-3 py-3">
                        <DealerOrders dealerId={d.id} dateFrom={dateFrom} dateTo={dateTo}
                          navigate={navigate} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Итог */}
              {dealers.length > 0 && (
                <div className="grid grid-cols-7 gap-2 items-center px-3 py-3 bg-gray-900 rounded-xl border border-gray-700 mt-2">
                  <div className="col-span-2 text-sm font-medium text-gray-400">ИТОГО</div>
                  <div className="text-center font-medium text-gray-300 mono">
                    {dealers.reduce((s, d) => s + +d.orders_count, 0)}
                  </div>
                  <div className="text-right mono font-medium text-gray-200">
                    {money(dealers.reduce((s, d) => s + +d.orders_amount, 0))}
                  </div>
                  <div className="text-right mono font-medium text-green-400">
                    {money(dealers.reduce((s, d) => s + +d.orders_paid, 0))}
                  </div>
                  <div className="text-right mono font-medium text-red-400">
                    {money(dealers.reduce((s, d) => s + +d.orders_debt, 0))}
                  </div>
                  <div></div>
                </div>
              )}
            </div>
          )}

          {/* ── ДЕТАЛИЗАЦИЯ ЗАКАЗОВ ── */}
          {tab === 'orders' && (
            <div className="space-y-4">
              {/* Фильтры */}
              <div className="flex flex-wrap gap-2">
                {isProducer && (
                  <select className="input py-1.5 text-sm w-44"
                    value={filterDealer} onChange={e => setFilterDealer(e.target.value)}>
                    {DEALER_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                <select className="input py-1.5 text-sm w-44"
                  value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button className="btn-primary text-sm py-1.5" onClick={loadOrders}>Применить</button>
              </div>

              {/* Итоги фильтрации */}
              {orders.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <MetricCard title="Заказов" value={orders.length} icon={Package} />
                  <MetricCard title="Сумма к оплате" value={money(orders.reduce((s, o) => s + +o.price_dealer, 0))}
                    color="text-blue-300" icon={TrendingUp} />
                  <MetricCard title="Задолженность" value={money(orders.reduce((s, o) => s + o.debt, 0))}
                    color="text-red-400" icon={AlertCircle} />
                </div>
              )}

              <div className="card overflow-hidden">
                {orders.length === 0
                  ? <div className="flex justify-center py-12 text-gray-600">Нет данных</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="text-left px-3 py-3 text-xs text-gray-500 font-medium w-28">Номер</th>
                            <th className="text-left px-3 py-3 text-xs text-gray-500 font-medium">Название</th>
                            {isProducer && <th className="text-left px-3 py-3 text-xs text-gray-500 font-medium w-32">Дилер</th>}
                            <th className="text-left px-3 py-3 text-xs text-gray-500 font-medium w-28">Статус</th>
                            <th className="text-right px-3 py-3 text-xs text-gray-500 font-medium w-28">К оплате</th>
                            <th className="text-right px-3 py-3 text-xs text-gray-500 font-medium w-28">Оплачено</th>
                            <th className="text-right px-3 py-3 text-xs text-gray-500 font-medium w-28">Долг</th>
                            <th className="text-left px-3 py-3 text-xs text-gray-500 font-medium w-28">Создан</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map(o => {
                            const st = ORDER_STATUSES[o.status] || {};
                            return (
                              <tr key={o.id} className="table-row" onClick={() => navigate('/orders/' + o.id)}>
                                <td className="px-3 py-2.5"><span className="mono text-blue-400 text-xs">{o.order_number}</span></td>
                                <td className="px-3 py-2.5 text-gray-200 font-medium max-w-xs truncate">{o.title}</td>
                                {isProducer && <td className="px-3 py-2.5 text-gray-400 text-xs">{o.dealer_name}</td>}
                                <td className="px-3 py-2.5"><Badge className={st.color}>{st.label}</Badge></td>
                                <td className="px-3 py-2.5 text-right mono text-gray-300">{money(o.price_dealer)}</td>
                                <td className="px-3 py-2.5 text-right mono text-green-400">{money(o.paid)}</td>
                                <td className={'px-3 py-2.5 text-right mono font-medium ' + (o.debt > 0 ? 'text-red-400' : 'text-gray-600')}>
                                  {o.debt > 0 ? money(o.debt) : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600">{fmt(o.created_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Раскрываемая детализация по дилеру
function DealerOrders({ dealerId, dateFrom, dateTo, navigate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { dealer_id: dealerId };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to = dateTo;
    api.get('/reports/orders', { params })
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealerId, dateFrom, dateTo]);

  if (loading) return <div className="flex justify-center py-4"><Spinner size={16} /></div>;
  if (!orders.length) return <p className="text-xs text-gray-600 py-2">Нет заказов</p>;

  return (
    <div className="space-y-1">
      {orders.map(o => {
        const st = ORDER_STATUSES[o.status] || {};
        return (
          <div key={o.id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-gray-800/50 cursor-pointer transition-colors"
            onClick={() => navigate('/orders/' + o.id)}>
            <span className="mono text-blue-400 text-xs w-24 shrink-0">{o.order_number}</span>
            <span className="text-sm text-gray-300 flex-1 truncate">{o.title}</span>
            <Badge className={st.color + ' shrink-0'}>{st.label}</Badge>
            <span className="mono text-xs text-gray-400 w-24 text-right shrink-0">{money(o.price_dealer)}</span>
            {o.debt > 0
              ? <span className="mono text-xs text-red-400 w-24 text-right shrink-0">-{money(o.debt)}</span>
              : <span className="mono text-xs text-green-500 w-24 text-right shrink-0">✓</span>
            }
          </div>
        );
      })}
    </div>
  );
}
